import { del, get, post, put } from '@api/api-client';

export type SiteAuthMode = 'sso' | 'password';

export interface SiteResponse {
  id: string;
  code: string;
  name: string;
  countryId: string;
  authMode: SiteAuthMode;
  pmsType: string | null;
  votingBaseUrl: string | null;
}

export interface CreateSitePayload {
  code: string;
  name: string;
  countryId: string;
  authMode: SiteAuthMode;
  pmsType?: string | null;
  votingBaseUrl?: string | null;
}

export interface UpdateSitePayload {
  code?: string;
  name?: string;
  pmsType?: string | null;
  votingBaseUrl?: string | null;
}

/** Lists sites, optionally narrowed to one country. */
export function listSites(countryId?: string): Promise<SiteResponse[]> {
  const query = countryId ? `?countryId=${encodeURIComponent(countryId)}` : '';
  return get<SiteResponse[]>(`/sites${query}`);
}

/** Creates a site. Superadmin only. */
export function createSite(payload: CreateSitePayload): Promise<SiteResponse> {
  return post<SiteResponse>('/sites', payload);
}

/**
 * Updates a site. Superadmin only.
 *
 * Country and auth mode are immutable server-side: both decide what the site
 * owns and how its voters sign in, so changing either would strand existing
 * editions and voters.
 */
export function updateSite(id: string, payload: UpdateSitePayload): Promise<SiteResponse> {
  return put<SiteResponse>(`/sites/${id}`, payload);
}

/** Deletes a site. Refused by the API while the site still owns any data. */
export function deleteSite(id: string): Promise<void> {
  return del<void>(`/sites/${id}`);
}
